import { LibraryCache } from 'base/library_cache';
import { PublicComponent } from 'base/library_service';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { RedErrorText } from 'base/ui/form';
import { FormInput } from 'base/ui/input';
import { Rows } from 'base/ui/layout';
import { DividerLine } from 'base/ui/panel';
import { TextSmall, TextSmallLight, TextTinyLight } from 'base/ui/typography';
import React from 'react';
import { anElementInstance, anInlineSection, aPage } from '../model';
import { SelectionStore } from '../selection/store';

type Props = {
  library: LibraryCache;
  selection: SelectionStore;
};

export function AddBySearchDevTool({ library, selection }: Props) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        label="Add many by search (dev tool)"
      />
      {open && (
        <Dialog title="Add many by search" onClose={() => setOpen(false)}>
          <Form
            library={library}
            selection={selection}
            onDone={() => setOpen(false)}
          />
        </Dialog>
      )}
    </>
  );
}

function Form({ library, selection, onDone }: Props & { onDone(): void }) {
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState<undefined | PublicComponent[]>();
  const [error, setError] = React.useState<unknown>();

  React.useEffect(
    () => {
      setResults(undefined);
      setError(undefined);
      let cancelled = false;
      library
        .findPublicComponents({ search, filterKind: 'component', limit: 100 })
        .then((r) => !cancelled && setResults(r.components))
        .catch((e) => !cancelled && setError(e));
      return () => void (cancelled = true);
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search],
  );

  function addAll(n: number) {
    const [page] = selection.project.homePage.subPages.push(
      aPage({
        name: `All matching: ${search}`,
      }),
    );
    results?.slice(0, n + 1).forEach((comp) => {
      page.sections.push(
        anInlineSection({
          name: comp.name,
          element: {
            current: anElementInstance({
              source: { slug: comp.componentSlug },
            }),
          },
        }),
      );
    });
    selection.set(selection.factory.forPage(page));
    onDone();
  }

  return (
    <Rows spacing={8}>
      <TextSmallLight>
        Creates a new page with all the components matching a search query. Try
        searching the slug of a page in the webflow master file.
      </TextSmallLight>
      <TextSmallLight>
        <FormInput
          type="search"
          placeholder="Search..."
          value={search}
          onChange={setSearch}
        />
      </TextSmallLight>
      <div />
      <DividerLine />
      <div />
      <RedErrorText error={error} />
      {!error && !results && (
        <TextSmallLight color="grey">Loading</TextSmallLight>
      )}
      {results && results.length === 0 && (
        <TextSmallLight color="red">Zero results</TextSmallLight>
      )}
      {results && results.length > 0 && (
        <>
          <TextSmallLight>Click to add all above (inclusive)</TextSmallLight>
          {results.map((comp, i) => (
            <Button key={i} onClick={() => addAll(i)}>
              <Rows spacing={4} xalign="start">
                <TextSmall>
                  {i + 1} - {comp.name}
                </TextSmall>
                <TextTinyLight>
                  {comp.categorySlug}/{comp.componentSlug}
                </TextTinyLight>
              </Rows>
            </Button>
          ))}
        </>
      )}
    </Rows>
  );
}
